import React, { useCallback, useMemo, useState } from "react";
import Grid from "@/lib/organisms/Grid";
import { CustomCellRendererProps } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { useAppData } from "@/hooks/useAppData";
import { Link } from "react-router-dom";
import { PencilIcon, PlusIcon } from "lucide-react";
import AddOwnerDialog from "@/lib/shadcn/components/add-owner-dialog";
import { ApartmentOwner } from "@/models/responses";
import { useAddApartmentOwnerMutation, useUpdateApartmentOwnerMutation } from "@/store/booking-management-api";
import { toast } from "@/lib/shadcn/hooks/use-toast";
import { Button } from "@/lib/shadcn/ui/button";
import { useAllApartmentEntities } from "@/store/slices/apartments";
import { useApartmentFilters } from "@/hooks/useApartmentFilters";
import ApartmentFilterBar from "@/lib/components/apartment-filter-bar";

interface ApartmentOwnerRow {
    id: number;
    taxId: string;
    fullName: string;
    taxisUsername: string;
    taxisPassword: string;
    apartments: { apartmentId: number; percentage: number }[];
}

interface ExtendedActionsRendererProps extends CustomCellRendererProps<ApartmentOwnerRow> {
    onEdit: (owner: ApartmentOwnerRow) => void;
}

const ActionsRenderer = ({ data, onEdit }: ExtendedActionsRendererProps) => {
    if (!data) return null;
    return (
        <Button variant="ghost" className="h-8 w-8 p-0" onClick={() => onEdit(data)}>
            <PencilIcon className="h-4 w-4" />
        </Button>
    );
};

const ApartmentNamesLinkRenderer = (params: CustomCellRendererProps<ApartmentOwnerRow>) => {
    const { apartmentIDMap } = useAppData();

    return (
        <>
            {params.data?.apartments?.map((apartmentOwnership, index) => {
                const apartmentId = apartmentOwnership.apartmentId;
                const percentage = apartmentOwnership.percentage === 100 ? "" : ` (${apartmentOwnership.percentage.toFixed(0)}%)`;
                const apartment = apartmentIDMap.get(apartmentId)?.name || "N/A";
                return (
                    <div key={index} className="inline-flex">
                        <Link className="text-blue-900 hover:text-blue-950 hover:underline me-5" to={"/admin/apartments/" + apartmentId}>
                            {apartment + percentage}
                        </Link>
                    </div>
                );
            })}
        </>
    );
};

export function OwnersPage(): React.ReactElement {
    const { owners, ownerApartmentsMap } = useAppData();
    const apartmentEntities = useAllApartmentEntities();
    const { predicates } = useApartmentFilters();
    const [showAddOwnerModal, setShowAddOwnerModal] = useState(false);
    const [ownerToEdit, setOwnerToEdit] = useState<ApartmentOwner | undefined>(undefined);
    const handleModalClosed = useCallback(() => {
        setOwnerToEdit(undefined);
        setShowAddOwnerModal(false);
    }, []);
    const [updateApartmentOwner] = useUpdateApartmentOwnerMutation();
    const [addApartmentOwner] = useAddApartmentOwnerMutation();

    const handleModalSubmitted = useCallback(
        async (fullName: string, taxisUsername: string, taxisPassword: string, taxId: string) => {
            const owner: ApartmentOwner = {
                id: ownerToEdit?.id || 0,
                fullName: fullName,
                taxisUserName: taxisUsername,
                taxisPassword: taxisPassword,
                taxId: taxId,
            };

            const addOrUpdate = ownerToEdit ? updateApartmentOwner : addApartmentOwner;

            try {
                await addOrUpdate(owner).unwrap();
                toast({
                    title: "Success",
                    description: `Owner ${ownerToEdit ? "updated" : "added"} successfully`,
                    variant: "success",
                });
                handleModalClosed();
            } catch (e) {
                toast({
                    title: "Error",
                    description: `Failed to ${ownerToEdit ? "update" : "add"} owner`,
                    variant: "destructive",
                });
                console.error(`Failed to ${ownerToEdit ? "update" : "add"} owner:`, e);
            }
        },
        [addApartmentOwner, updateApartmentOwner, ownerToEdit, handleModalClosed],
    );
    const columnDefinitions: ColDef<ApartmentOwnerRow>[] = [
        {
            field: "id",
            headerName: "Id",
            width: 50,
            cellDataType: "number",
            filter: false,
        },
        {
            field: "fullName",
            headerName: "Name",
            cellDataType: "text",
            filter: "agTextColumnFilter",
            wrapText: true,
            width: 300,
        },
        {
            field: "taxId",
            headerName: "Tax Id",
            cellDataType: "text",
            wrapText: true,
            width: 150,
            filter: false,
        },
        {
            field: "taxisUsername",
            headerName: "Taxis Username",
            cellDataType: "text",
            wrapText: true,
            width: 150,
            filter: false,
        },
        {
            field: "taxisPassword",
            headerName: "Taxis Password",
            cellDataType: "text",
            wrapText: true,
            width: 150,
            filter: false,
        },
        {
            field: "apartments",
            headerName: "Apartments",
            flex: 400,
            cellDataType: "text",
            filter: "agTextColumnFilter",
            wrapText: true,
            cellRenderer: ApartmentNamesLinkRenderer,
        },
        {
            headerName: "Actions",
            width: 90,
            cellRenderer: ActionsRenderer,
            filter: false,
            cellRendererParams: {
                onEdit: (owner: ApartmentOwnerRow) => {
                    setOwnerToEdit({
                        id: owner.id,
                        fullName: owner.fullName,
                        taxisUserName: owner.taxisUsername,
                        taxisPassword: owner.taxisPassword,
                    });
                    setShowAddOwnerModal(true);
                },
            },
        },
    ];

    const ownerRows: ApartmentOwnerRow[] = useMemo<ApartmentOwnerRow[]>(() => {
        return owners
            .filter((owner) => {
                if (predicates.length === 0) {
                    return true;
                }
                const ownedApartments = ownerApartmentsMap.get(owner.id) || [];
                // An owner is shown when at least one of their apartments matches
                // every active predicate. Apartments whose entity is not loaded
                // (deleted / inactive) cannot satisfy any predicate, so they
                // contribute nothing — matches the per-row predicate semantics
                // already in use across Tier 3 pages.
                return ownedApartments.some((ownership: { apartmentId: number; percentage: number }) => {
                    const apartment = apartmentEntities[ownership.apartmentId];
                    if (!apartment) {
                        return false;
                    }
                    return predicates.every((predicate) => predicate(apartment));
                });
            })
            .map((owner) => ({
                id: owner.id,
                fullName: owner.fullName,
                taxisUsername: owner.taxisUserName || "",
                taxisPassword: owner.taxisPassword || "",
                taxId: owner.taxId || "",
                apartments: ownerApartmentsMap.get(owner.id) || [],
            }));
    }, [owners, ownerApartmentsMap, predicates, apartmentEntities]);

    return (
        <div className="flex flex-col space-y-6 p-2 py-0" data-testid="owners-page">
            <ApartmentFilterBar />
            <AddOwnerDialog
                open={showAddOwnerModal}
                onClose={handleModalClosed}
                onSubmit={handleModalSubmitted}
                existingOwner={ownerToEdit}
            />
            <Grid<ApartmentOwnerRow>
                columnDefs={columnDefinitions}
                gridName={"Apartments Owners"}
                initialRowData={ownerRows.sort((a, b) => a.fullName.localeCompare(b.fullName))}
                getRowId={(row) => row.data.fullName.toString()}
                canExport={false}
                buttons={[
                    {
                        label: "Add Owner",
                        onClick: () => setShowAddOwnerModal(true),
                        icon: PlusIcon,
                    },
                ]}
            />
        </div>
    );
}
